export const aboutus2 = {
  seo: {
    title: "Bespire - About Us",
    description: "Bespire's mission is to provide every content creator access to professional tools and local support.",
    keywords: "creator monetization, AI tools for creators, European creator platform, sell digital products, subscription tools"
  },
  navigation: {
    home: "Home",
    features: "Features",
    about: "About",
    contact: "Contact",
    login: "Login",
    benefits: "Benefits",
    getStarted: "Get Started",
    pricingAndExperiences: "Pricing & Experiences"
  },
  hero: {
    title: "Empowering Creators,",
    subtitle: "Everywhere",
    description: "Bespire helps European and MENA creators turn passion into sustainable income with AI-powered tools and local expertise.",
    cta: "Join Us",
    learnMore: "Learn More"
  },
  mission: {
    title: "Our Mission",
    description: "Bespire's mission is to provide every content creator access to professional tools to quickly and efficiently monetize their work – with local payment solutions, language support, and artificial intelligence. We believe creativity knows no boundaries, and every creator deserves the right to success."
  },
  story: {
    title: "Our Story",
    timeline: [
      {
        year: "2023",
        title: "The Beginning",
        description: "We founded Bespire with the goal of solving European creators' biggest challenge: the lack of local payment solutions and language support."
      },
      {
        year: "2024",
        title: "AI Integration",
        description: "We introduced our AI-powered website builder that enables creators to build professional websites in minutes."
      },
      {
        year: "Future",
        title: "Community Building",
        description: "We continuously expand our platform with new features and tools to support the growth of the creator community."
      }
    ]
  },
  values: {
    title: "Our Values",
    items: [
      {
        title: "Innovation",
        description: "AI-powered solutions that make complex processes simple",
        icon: "Brain"
      },
      {
        title: "Locality",
        description: "Support for local languages and payment methods in every region",
        icon: "Globe"
      },
      {
        title: "Simplicity",
        description: "Quick start and easy use, no technical knowledge required",
        icon: "Zap"
      },
      {
        title: "Community",
        description: "Strong creator ecosystem and supportive environment",
        icon: "Users"
      }
    ]
  },
  achievements: {
    title: "Milestones",
    subtitle: "We're proud of the results we've achieved so far",
    items: [
      {
        title: "AI-first platform",
        description: "The first European platform offering AI-powered website creation for creators"
      },
      {
        title: "Multi-regional support",
        description: "Active service in 15+ countries with local payment methods and language support"
      },
      {
        title: "Rapid growth",
        description: "300% annual growth in users and revenue across all markets"
      },
      {
        title: "Community building",
        description: "Active creator community with workshops, events, and mentoring programs"
      }
    ]
  },
  whyChooseUs: {
    title: "Why Choose Us?",
    subtitle: "What makes Bespire special and different from every other platform",
    items: [
      {
        title: "AI-powered simplicity",
        description: "While others offer complex tools, we use AI power to make website creation and selling simple and effortless.",
        icon: "Brain"
      },
      {
        title: "Local expertise",
        description: "Local payment methods, languages and cultural knowledge in 15+ countries - not just a copy of a global solution.",
        icon: "Globe"
      },
      {
        title: "Dedicated creator support",
        description: "Tools specifically designed for creators and personal mentoring - not a generic e-commerce platform.",
        icon: "Users"
      },
      {
        title: "Fast results",
        description: "Working website within 24 hours and immediate selling capability - not months of waiting for development.",
        icon: "Zap"
      }
    ]
  },
  contact: {
    title: "Get in Touch",
    subtitle: "We're ready to help you on your creator journey",
    description: "Whatever questions you have or help you need, our team is ready to support you.",
    methods: [
      {
        title: "Email Support",
        description: "hello@bespire.com",
        details: "Response time: 2-4 hours on weekdays"
      },
      {
        title: "Live Chat",
        description: "Instant help",
        details: "Monday-Friday 9:00-18:00"
      },
      {
        title: "Community",
        description: "Discord server",
        details: "Join the active creator community"
      }
    ]
  },
  statistics: {
    title: "Our Results in Numbers",
    subtitle: "We're proud of our creator community's success",
    items: [
      {
        number: "15,000+",
        label: "Active Creators",
        description: "Across Europe and MENA"
      },
      {
        number: "€2.5M+",
        label: "Revenue Generated",
        description: "Total creator earnings"
      },
      {
        number: "95%",
        label: "Satisfaction Rate",
        description: "Creators recommend us"
      },
      {
        number: "24h",
        label: "Average Setup Time",
        description: "From idea to live site"
      }
    ]
  },
  testimonials: {
    title: "What Our Creators Say",
    subtitle: "Real stories from real creators",
    items: [
      {
        name: "Anna Kovács",
        role: "Lifestyle Blogger",
        content: "With Bespire, I've grown my revenue 10x in just 3 months. The AI assistant is brilliant - my site is ready in seconds.",
        location: "Budapest, Hungary"
      },
      {
        name: "Ahmed Hassan",
        role: "Fitness Coach",
        content: "Finally a platform that understands the MENA market. Local payment methods and Arabic support made all the difference.",
        location: "Dubai, UAE"
      },
      {
        name: "Marie Dubois",
        role: "Artist & Designer",
        content: "Je peux enfin vendre mes créations facilement. L'interface est intuitive et le support client exceptionnel.",
        location: "Paris, France"
      }
    ]
  },
  vision: {
    title: "Our Vision",
    subtitle: "Building the future of the creator economy",
    description: "By 2030, Bespire will be the leading creator platform in Europe and the MENA region. Our goal is to ensure every talented person has the opportunity to turn their passion into a sustainable business - regardless of where they live or what language they speak.",
    goals: [
      "1 million active creators on the platform",
      "Support for 50+ languages and local payment methods",
      "AI-powered mentoring for every creator",
      "Carbon-neutral operations by 2028"
    ]
  },
  team: {
    title: "Our Team",
    subtitle: "Experienced professionals who believe in the future of the creator economy",
    members: [
      {
        name: "Peter Kovács",
        role: "Founder & CEO",
        description: "10 years of experience in the tech industry, former leadership positions at international startups."
      },
      {
        name: "Anna Nagy",
        role: "AI & Product Lead",
        description: "Artificial intelligence expert, previously worked on AI solutions at leading tech companies."
      },
      {
        name: "Mark Szabó",
        role: "Head of Creator Success",
        description: "Creator industry expert, helps creators maximize their revenue on the platform."
      },
      {
        name: "Eszter Tóth",
        role: "Head of Growth",
        description: "Marketing and growth strategy expert, responsible for the platform's international expansion."
      }
    ]
  },
  cta: {
    title: "Join Us!",
    description: "Start making money with your passion today. Get started in minutes with our AI-powered tools."
  },
  footer: {
    description: "Empowering creators everywhere with AI-powered tools and local expertise.",
    platform: "Platform",
    platformLinks: {
      home: "Home",
      aiAssistant: "AI Assistant",
      pricing: "Pricing"
    },
    support: "Support",
    supportLinks: {
      help: "Help",
      documentation: "Documentation",
      contact: "Contact"
    },
    legal: "Legal",
    legalLinks: {
      terms: "Terms of Service",
      privacy: "Privacy Policy",
      cookies: "Cookie Policy"
    },
    copyright: "© 2024 Bespire. All rights reserved.",
    socialAlert: "Instagram profile coming soon",
    twitterAlert: "Twitter profile coming soon",
    subscribeAlert: "Thank you for subscribing! We'll notify you about news via email soon.",
    pricing: "Pricing",
    partnerProgram: "Partner Program",
    referralProgram: "Referral Program",
    aboutUs: "About Us"
  }
};